import { useState } from 'react'
import { X, TrendingUp, CreditCard, Info } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/useI18n'
import type { TopUpBreakdown } from '../hooks/useAutoTopUp'

/**
 * #1190 Phase 4: 「YOKOSO 通貨払い時に残高不足 → 不足分をチャージ + 全額支払う」
 * を 1 tap で承認させる bottom sheet。
 *
 * デモ用途限定 (isYokosoPointsEnabled() の gate 下でのみ表示される)。
 * 実運用の非同期 top-up は本 sheet の設計と噛み合わないため、フローが安定
 * したら別 issue で再設計する。
 */
export default function AutoTopUpSheet({
  open,
  breakdown,
  storeName,
  onCancel,
  onConfirm,
}: {
  open: boolean
  breakdown: TopUpBreakdown | null
  storeName: string
  onCancel: () => void
  onConfirm: () => Promise<void>
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open || !breakdown) return null

  const handleConfirm = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[115] flex items-end bg-black/60"
      style={{ maxWidth: 430, margin: '0 auto' }}
    >
      <div className="animate-slide-up w-full rounded-t-3xl bg-white p-5 pb-8">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-text-primary">
            {t('autoTopUp.title')}
          </h3>
          <button
            onClick={onCancel}
            disabled={submitting}
            className="tap-effect p-1"
            aria-label={t('common.close')}
          >
            <X size={20} className="text-text-tertiary" />
          </button>
        </div>

        <p className="mb-4 font-body text-sm text-text-secondary">
          {t('autoTopUp.subtitle', {
            store: storeName,
            amount: breakdown.billAmount.toLocaleString(),
          })}
        </p>

        {/* 内訳 */}
        <div className="mb-4 rounded-xl border border-yellow-200 bg-yellow-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <TrendingUp size={16} className="text-yellow-800" />
            <span className="font-display text-xs font-semibold uppercase tracking-wide text-yellow-900">
              {t('autoTopUp.shortfallLabel')}
            </span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-body text-text-secondary">
                {t('autoTopUp.currentBalance')}
              </span>
              <span className="font-mono text-text-primary">
                ¥ {breakdown.balance.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-body text-text-secondary">
                {t('autoTopUp.billAmount')}
              </span>
              <span className="font-mono text-text-primary">
                ¥ {breakdown.billAmount.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-yellow-200 pt-1.5">
              <span className="font-body font-semibold text-yellow-900">
                {t('autoTopUp.shortfall')}
              </span>
              <span className="font-mono font-semibold text-yellow-900">
                ¥ {breakdown.topupAmount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Charge 明細 (常にクレジットカード即時決済) */}
        <div className="mb-4 rounded-xl bg-gray-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <CreditCard size={16} className="text-navy" />
            <span className="font-display text-xs font-semibold uppercase tracking-wide text-navy">
              {t('autoTopUp.chargeDetail')}
            </span>
          </div>
          {/* 経路の説明: card 4.4% + ¥350 の理由と、なぜ他経路 (bank/Revolut/Alipay) が
             使えないかを明示。「即時性が必要 = card しか成立しない」の trade-off。 */}
          <p className="mb-2 font-body text-[11px] leading-5 text-text-secondary">
            {t('autoTopUp.methodExplain')}
          </p>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-body text-text-secondary">
                {t('autoTopUp.chargeSubtotal')}
              </span>
              <span className="font-mono text-text-primary">
                ¥ {breakdown.topupAmount.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-body text-text-secondary">
                {t('autoTopUp.chargeFee')}
              </span>
              <span className="font-mono text-text-primary">
                ¥ {breakdown.topupFee.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 pt-1.5">
              <span className="font-body font-semibold text-text-primary">
                {t('autoTopUp.chargeTotal')}
              </span>
              <span className="font-mono font-semibold text-text-primary">
                ¥ {breakdown.totalCharged.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* 代替経路の案内: 手数料を抑えたい客への逃がし導線。「事前チャージなら
           1.5〜2.5%」を明示 + ウォレット /charge へ 1 tap で遷移させる。 */}
        <div className="mb-3 rounded-xl border border-matcha/30 bg-matcha/5 p-3">
          <div className="mb-2 flex items-center gap-2">
            <Info size={14} className="text-matcha shrink-0" />
            <span className="font-display text-[11px] font-semibold text-matcha">
              {t('autoTopUp.altRailsTitle')}
            </span>
          </div>
          <p className="font-body text-[11px] leading-5 text-text-secondary">
            {t('autoTopUp.altRailsBody')}
          </p>
          <button
            type="button"
            onClick={() => {
              onCancel()
              navigate('/charge')
            }}
            disabled={submitting}
            className="tap-effect mt-2 h-9 w-full rounded-lg border border-matcha bg-white font-display text-[11px] font-semibold text-matcha"
          >
            {t('autoTopUp.prechargeCta')}
          </button>
        </div>

        <p className="mb-3 font-body text-xs leading-5 text-text-secondary">
          {t('autoTopUp.note')}
        </p>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="font-body text-xs text-red-600 break-all">{error}</p>
          </div>
        )}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          className="tap-effect h-14 w-full rounded-xl bg-yokoso-red font-display text-base font-semibold text-white shadow-button disabled:opacity-50"
        >
          {submitting
            ? t('autoTopUp.submitting')
            : t('autoTopUp.confirmCta', {
                amount: breakdown.totalCharged.toLocaleString(),
              })}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="tap-effect mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white font-display text-sm text-text-secondary"
        >
          {t('autoTopUp.cancel')}
        </button>
      </div>
    </div>
  )
}
